import { z } from 'zod';

import { LocalDateSchema } from '../admin/attendance';
import { IsoDateTimeSchema, NullableIsoDateTimeSchema } from '../common/datetime';
import { JustificationId } from '../common/ids';
import {
  JUSTIFICATION_MAX_PER_DAY_KIND,
  JUSTIFICATION_REASON_MAX,
  JustificationCancelReasonSchema,
  JustificationKindSchema,
  JustificationOriginSchema,
  JustificationStatusSchema,
} from '../common/schedules';

import { IdempotencyKeySchema } from './vpp';

/**
 * 008 · justificaciones, realm staff (contracts/staff-api.md S2–S4).
 *
 * El trabajador justifica tardanzas, salidas anticipadas y faltas de sus días; ve el estado y puede
 * cancelar las pendientes. Sin nombres ni ids de administradores, sin clave de idempotencia en las
 * respuestas y sin `worker_id`/`company_id` (DTO campo a campo en `justifications/mapper.ts`).
 * El parámetro `{id}` usa `JustificationIdParamsSchema` (`admin/justifications.ts`).
 */

/** Motivo del trabajador: 1–300 tras `trim`. */
export const StaffJustificationReasonSchema = z
  .string()
  .trim()
  .min(1, { message: 'Cuéntanos qué pasó' })
  .max(JUSTIFICATION_REASON_MAX);

/** `POST /attendance/justifications`. */
export const CreateStaffJustificationBodySchema = z.object({
  day: LocalDateSchema,
  kind: JustificationKindSchema,
  reason: StaffJustificationReasonSchema,
  idempotency_key: IdempotencyKeySchema,
});
export type CreateStaffJustificationBody = z.infer<typeof CreateStaffJustificationBodySchema>;

export const StaffJustificationSchema = z.object({
  id: JustificationId,
  day: LocalDateSchema,
  kind: JustificationKindSchema,
  status: JustificationStatusSchema,
  /** `ADMIN` ⇒ "La registró tu administrador". */
  origin: JustificationOriginSchema,
  observed_minutes: z.number().int().nonnegative().nullable(),
  reason: z.string(),
  requested_at: IsoDateTimeSchema,
  resolved_at: NullableIsoDateTimeSchema,
  /** Nota al aprobar o motivo del rechazo. */
  resolution_note: z.string().nullable(),
  cancel_reason: JustificationCancelReasonSchema.nullable(),
  still_applies: z.boolean(),
  can_cancel: z.boolean(),
});
export type StaffJustification = z.infer<typeof StaffJustificationSchema>;

/** `200` de `GET /attendance/justifications` (últimos 60 días, ≤ 100, recientes primero). */
export const StaffJustificationsResponseSchema = z.object({
  items: z.array(StaffJustificationSchema).max(100),
  counts: z.object({ pending: z.number().int().nonnegative() }),
  max_per_day_kind: z.literal(JUSTIFICATION_MAX_PER_DAY_KIND),
  server_time: IsoDateTimeSchema,
});
export type StaffJustificationsResponse = z.infer<typeof StaffJustificationsResponseSchema>;

/** `201` (creada) o `200` con `idempotent: true` (la clave ya la creó con el mismo contenido). */
export const StaffJustificationMutationResponseSchema = z.object({
  item: StaffJustificationSchema,
  idempotent: z.boolean(),
});
export type StaffJustificationMutationResponse = z.infer<
  typeof StaffJustificationMutationResponseSchema
>;

/** `200` de `POST /attendance/justifications/{id}/cancel` (body vacío). */
export const StaffJustificationCancelResponseSchema = z.object({ item: StaffJustificationSchema });
export type StaffJustificationCancelResponse = z.infer<
  typeof StaffJustificationCancelResponseSchema
>;
